// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Default styled web component.
 * @author James Reid
 */

// @ts-check

// @@imports-utils
import { cloneTemplate, createLucideIcon } from "../../utils/index.js"

// @@body
class StyledComponent extends HTMLElement {
    wrapper = /** @type {HTMLElement?} */ (null)

    /* eslint-disable jsdoc/no-undefined-types -- Type *is* defined. */
    /**
     * Construct a custom web component containing a shadow root which inherits
     * the parent document stylesheets, and any additional stylesheets passed
     * into the constructor.
     * @param {object} obj Optional destructured parameters object.
     * @param {string} [obj.templateId]
     * @param {boolean} [obj.wrapModifiers]
     * @param {string[]} [obj.modifiers]
     * @param {CSSStyleSheet[]} [obj.styleSheets] Additional styles to be added
     * to adopted stylesheets of the component shadow root.
     * @param {ShadowRootMode} [obj.mode] Shadow root mode to use.
     */
    /* eslint-enable jsdoc/no-undefined-types -- Rule enable pair. */
    constructor({
        templateId,
        wrapModifiers = true,
        modifiers = ["--hide-prices", "--hide-basket", "--hoverable"],
        styleSheets = [],
        mode = "open"
    } = {}) {
        super()

        // Generate parent style sheet from all CSS rules in document style
        // sheets.
        const parentStyleSheet = new CSSStyleSheet()
        for (const sheet of document.styleSheets) {
            // Add all style sheet rules to parent, ignoring cross origin style
            // sheets.
            try {
                for (const rule of sheet.cssRules) {
                    parentStyleSheet.insertRule(rule.cssText)
                }
            }
            catch { continue }
        }

        // Add shadow root and add generated adopted stylesheets.
        this.attachShadow({ mode });
        /** @type {ShadowRoot} */ (this.shadowRoot)
            .adoptedStyleSheets
            .push(parentStyleSheet, ...styleSheets)

        // Add body style modifier wrapper to shadow root if required,
        // duplicating all style modifier classes applied to document body.
        if (wrapModifiers) {
            this.wrapper = document.createElement("div")
            this.wrapper.classList.add("modifier-wrapper")
            for (const modifier of modifiers) {
                if (document.body.classList.contains(modifier)) {
                    this.wrapper.classList.add(modifier)
                }
            }
            /** @type {ShadowRoot} */ (this.shadowRoot).append(this.wrapper)
        }

        // Clone template and add to shadow root if ID specified, otherwise add
        // empty container to shadow root such that template property is always
        // populated.
        this.template = templateId ? cloneTemplate(templateId)
            : document.createElement("div")
        this.wrapper ? this.wrapper.append(this.template)
            : /** @type {ShadowRoot} */ (this.shadowRoot)
                .append(this.template)
        this.renderLucideIcons()
    }

    /**
     * Convert all icon elements in the shadow DOM with data-lucide icon name
     * values to SVG elements.
     */
    renderLucideIcons() {
        // Loop over all icon node in the shadow root, and generate an SVG
        // element using lucide where appropriate.
        /** @type {ShadowRoot} */ (this.shadowRoot)
            .querySelectorAll("i")
            .forEach(icon => {
                // Fetch data-lucide icon name from icon element.
                const iconName = icon.dataset.lucide
                if (!iconName) { return }

                // Replace icon element with SVG element if provided.
                const svg = createLucideIcon(iconName)
                if (svg) { icon.replaceWith(svg) }
            })
    }


    static get customName() {
        return "styled-component"
    }
}

// @@exports
export { StyledComponent }
